import cn from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import shuffle from 'lodash/shuffle';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ProgressBar } from 'react-step-progress-bar';

import { Checkbox } from '@webapp/common/components/checkbox';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { calcPercent } from '@webapp/common/lib/ui';
import { striptags } from '@webapp/common/lib/utils';
import { CANTRATE_ID } from '@webapp/common/resources/constants';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './conjoint.css';

export const Conjoint: FC<SurveyQuestion> = ({ question, readOnly }) => {
    const {
        conjointConfig,
        id: questionId,
        responseActor,
        params: { randomOrder, randomScreensOrder, cantRate, cantRateText },
    } = question;
    const timer = useRef(null);
    const [answeredData, setAnsweredData] = useState<Record<number, number>>(() => {
        // Поддержка восстановления незавершённого ответа: если response уже
        // содержит объект { [taskId]: cardId | CANTRATE_ID } — подхватываем его.
        const value = responseActor.getSnapshot().context.value;
        return value && typeof value === 'object' ? (value as Record<number, number>) : {};
    });
    const [activeTaskIndex, setActiveTaskIndex] = useState(0);

    const tasks = useMemo(() => {
        if (conjointConfig?.tasks) {
            let newTasks = cloneDeep(conjointConfig.tasks);

            if (randomScreensOrder) {
                newTasks = shuffle(newTasks);
            }
            if (randomOrder) {
                newTasks = newTasks.map((task) => ({
                    ...task,
                    cards: shuffle(task.cards),
                }));
            }

            return newTasks;
        }

        return [];
    }, [conjointConfig?.tasks, randomOrder, randomScreensOrder]);

    const activeTask = tasks[activeTaskIndex];
    const styles = useContext(CustomStylesCtx);
    const tasksLength = tasks.length;
    const {
        params: {
            alert: { conjointCardsText, conjointCompleteText },
        },
    } = useStateSelector(selectInfo);

    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    // Индикатор показывает количество отвеченных заданий, а не индекс текущего
    const answeredTasksCount = Object.keys(answeredData).length;

    const getPercent = useCallback(
        () => calcPercent(answeredTasksCount, tasks.length),
        [answeredTasksCount, tasks.length],
    );

    const onClickCard = (taskId: number, cardId: number | null) => () => {
        if (readOnly) {
            return;
        }

        setAnsweredData((prev) => {
            const result = { ...prev };

            // Снятие галочки «Не могу оценить»: возвращаем задание в неотвеченное.
            if (cardId === null) {
                delete result[taskId];
            } else {
                result[taskId] = cardId;
            }

            const isLastTask = !tasks[activeTaskIndex + 1];

            if (!isLastTask) {
                timer.current = setTimeout(() => {
                    setActiveTaskIndex(activeTaskIndex + 1);
                }, 200);
            }

            // CHANGE уходит, когда ответ покрывает все задания, либо когда снятие
            // галочки на последнем задании нарушило полноту уже отправленного ответа —
            // машине нужен актуальный (неполный) объект: validate считает
            // Object.keys(value) и неполный объект валидным ответом не считается.
            if (Object.keys(result).length === tasks.length || (cardId === null && isLastTask)) {
                responseActor.send({
                    type: 'CHANGE',
                    value: result as unknown as string,
                    questionId,
                });
            }

            return result;
        });
    };

    return (
        <div
            className={cn(css.conjoint, CssQuestionType.QUESTION, CssQuestionType.CONJOINT, {
                [css.conjointActive]: !readOnly,
            })}
            style={{
                // @ts-ignore
                '--active-color': styles?.fieldBorderColor30,
                '--hover-color': styles?.fieldBorderColor10,
            }}
        >
            {!conjointConfig || !activeTask ? (
                <div>Нет данных для отображения</div>
            ) : (
                <>
                    <div className={css.progressBarContainer}>
                        <div>{conjointCardsText}</div>
                        <div className={css.pairNumber}>
                            {answeredTasksCount}/{tasksLength}
                        </div>
                        <div className={css.progressBar}>
                            <ProgressBar
                                percent={getPercent()}
                                unfilledBackground='#ccc'
                                filledBackground={styles.elementColor}
                            />
                        </div>
                        {answeredTasksCount === tasksLength && <div>{conjointCompleteText}</div>}
                    </div>
                    <div className={css.cards}>
                        {activeTask.cards.map((card) => {
                            return (
                                <div
                                    onClick={onClickCard(activeTask.id, card.id)}
                                    className={cn(css.card, {
                                        [css.cardActive]: answeredData[activeTask.id] === card.id,
                                    })}
                                    key={card.id}
                                >
                                    <div className={css.cardName}>{striptags(card.name)}</div>
                                    <div className={css.cardLevels}>
                                        {card.levels.map((level) => (
                                            <div className={css.cardLevel} key={level.id}>
                                                {striptags(level.attributeName)} -{' '}
                                                <span>{striptags(level.levelName)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {cantRate && (
                        <div className={css.checkboxWrap}>
                            <Checkbox
                                checked={answeredData[activeTask.id] === CANTRATE_ID}
                                label={cantRateText}
                                onChange={(checked) => {
                                    onClickCard(activeTask.id, checked ? CANTRATE_ID : null)();
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
